import { useCallback, useRef } from 'react';

import type {
  DialogFlowInstance,
  DialogFlowStepComponent,
  DialogFlowStepProps,
  DialogFlowStepStatus,
} from './types';

export function useDialogFlow<T extends string | number | symbol = string>() {
  const dialogRef = useRef<DialogFlowInstance<T>>(null);

  const updateDialogProps = useCallback(
    (status: DialogFlowStepStatus<T>, dialogProps: DialogFlowStepProps) => {
      dialogRef.current?.updateDialogProps(status, dialogProps);
    },
    [],
  );

  const setStatus = useCallback(
    (status: DialogFlowStepStatus<T>, ...args: any[]) => {
      dialogRef.current?.setStatus(status, ...args);
    },
    [],
  );

  const setDialogState = useCallback((state: boolean) => {
    dialogRef.current?.setDialogState(state);
  }, []);

  const closeDialog = useCallback(() => {
    dialogRef.current?.closeDialog();
  }, []);

  const proceed = useCallback((...args: any[]) => {
    dialogRef.current?.proceed(...args);
  }, []);

  const drawback = useCallback((...args: any[]) => {
    dialogRef.current?.drawback(...args);
  }, []);

  const getStepsValue = useCallback(() => {
    return dialogRef.current?.getStepsValue() ?? {};
  }, []);

  const getStepValue = useCallback((key: DialogFlowStepStatus<T>) => {
    return dialogRef.current?.getStepValue(key);
  }, []);

  const getStatus = useCallback(() => {
    return dialogRef.current?.getStatus();
  }, []);

  const createStep = useCallback(
    <TProps = Record<string, unknown>>(
      step: DialogFlowStepComponent<TProps, T>,
    ): DialogFlowStepComponent<TProps, T> => {
      return step;
    },
    [],
  );

  return {
    dialogRef,
    createStep,
    updateDialogProps,
    setStatus,
    setDialogState,
    closeDialog,
    proceed,
    drawback,
    getStepsValue,
    getStepValue,
    getStatus,
  };
}
